import {
  Review,
  PaginatedResponse,
  ReviewListParams,
  ModerateReviewRequest,
  BulkModerateRequest,
  BulkModerateResponse,
} from '@/types';
import {
  getMockReviews,
  moderateMockReview,
  bulkModerateMockReviews,
} from './mock-data';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';
const IS_MOCK = import.meta.env.VITE_API_MOCK === 'true';
const MOCK_DELAY = 300; // simulate network latency

function mockDelay(): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, MOCK_DELAY));
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public data?: any
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

class ApiClient {
  private baseUrl: string;
  private getAccessToken?: () => Promise<string>;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  setAuthProvider(getAccessToken: () => Promise<string>) {
    this.getAccessToken = getAccessToken;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (this.getAccessToken) {
      try {
        const token = await this.getAccessToken();
        headers['Authorization'] = `Bearer ${token}`;
      } catch (error) {
        console.error('Failed to get access token:', error);
        throw new ApiError(401, 'Authentication failed');
      }
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(
        response.status,
        errorData.message || `HTTP ${response.status}: ${response.statusText}`,
        errorData
      );
    }

    return response.json();
  }

  // Review endpoints
  async getReviews(
    params: ReviewListParams
  ): Promise<PaginatedResponse<Review>> {
    if (IS_MOCK) {
      await mockDelay();
      return getMockReviews(params);
    }

    const queryParams = new URLSearchParams();

    if (params.page) queryParams.append('page', params.page.toString());
    if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    if (params.search) queryParams.append('search', params.search);

    if (params.status) {
      params.status.forEach(s => queryParams.append('status', s));
    }
    if (params.moderationStatus) {
      params.moderationStatus.forEach(ms => queryParams.append('moderationStatus', ms));
    }
    if (params.flagTypes) {
      params.flagTypes.forEach(ft => queryParams.append('flagTypes', ft));
    }
    if (params.rating) {
      params.rating.forEach(r => queryParams.append('rating', r.toString()));
    }
    if (params.dateFrom) queryParams.append('dateFrom', params.dateFrom);
    if (params.dateTo) queryParams.append('dateTo', params.dateTo);

    return this.request<PaginatedResponse<Review>>(
      `/reviews?${queryParams.toString()}`
    );
  }

  async getReview(reviewId: string): Promise<Review> {
    if (IS_MOCK) {
      await mockDelay();
      const result = getMockReviews({ page: 1, pageSize: 100 });
      const review = result.items.find((r) => r.reviewId === reviewId);
      if (!review) throw new ApiError(404, 'Review not found');
      return review;
    }

    return this.request<Review>(`/reviews/${reviewId}`);
  }

  async moderateReview(request: ModerateReviewRequest): Promise<Review> {
    if (IS_MOCK) {
      await mockDelay();
      return moderateMockReview(request.reviewId, request.action);
    }

    return this.request<Review>(`/reviews/${request.reviewId}/moderate`, {
      method: 'POST',
      body: JSON.stringify({
        action: request.action,
        notes: request.notes,
      }),
    });
  }

  async bulkModerateReviews(
    request: BulkModerateRequest
  ): Promise<BulkModerateResponse> {
    if (IS_MOCK) {
      await mockDelay();
      return bulkModerateMockReviews(request.reviewIds, request.action);
    }

    return this.request<BulkModerateResponse>('/reviews/bulk-moderate', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  // Health check
  async healthCheck(): Promise<{ status: string }> {
    return this.request<{ status: string }>('/health');
  }
}

export const apiClient = new ApiClient(API_BASE_URL);
